/**
 * RelatedLinksSection — manage cross-axis peer (related) links for a node.
 *
 * Lists existing peer connections with colored axis badges and remove buttons.
 * "Add Related" opens a searchable dropdown filtered to nodes not already linked.
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { X, Plus, ChevronDown } from 'lucide-react';
import type { AdjacencyNode } from '../../types/adjacency';
import { INITIATIVE_COLORS } from '../../utils/map-colors';

interface RelatedLinksSectionProps {
  node: AdjacencyNode;
  allNodes: AdjacencyNode[];
  onAdd: (targetId: string) => void;
  onRemove: (targetId: string) => void;
}

/** Axis badge color mapping (reuses initiative colors loosely). */
const AXIS_COLORS: Record<string, string> = {
  'New Services': '#3b82f6',
  'New Markets & Customers': '#059669',
  'Forward Integration': '#d97706',
  'New Technologies & Capabilities': '#7c3aed',
  'New Channels': '#ec4899',
  'New Geographies': '#dc2626',
  'Backward Integration': '#0891b2',
  'New Products': '#f59e0b',
  'Core': '#6b7280',
};

export function RelatedLinksSection({ node, allNodes, onAdd, onRemove }: RelatedLinksSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const peerEdges = node.edges.filter(e => e.relationship === 'peer');
  const peerNodes = peerEdges
    .map(e => allNodes.find(n => n.id === e.targetId))
    .filter((n): n is AdjacencyNode => n != null);

  // Nodes not already linked
  const linkedIds = new Set(node.edges.map(e => e.targetId));
  const available = allNodes.filter(n => n.id !== node.id && !linkedIds.has(n.id));

  const filtered = useMemo(() => {
    if (!search) return available;
    const q = search.toLowerCase();
    return available.filter(n => n.name.toLowerCase().includes(q));
  }, [available, search]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  return (
    <div>
      {/* Peer node list */}
      {peerNodes.length > 0 ? (
        <div className="flex flex-wrap gap-1 mb-2">
          {peerNodes.map(n => (
            <span
              key={n.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px]"
              style={{
                background: 'var(--glass-bg-elevated)',
                border: '1px solid var(--glass-border)',
                color: 'var(--text-secondary)',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: INITIATIVE_COLORS[n.strategicInitiative] }} />
              {n.name.length > 18 ? n.name.slice(0, 17) + '\u2026' : n.name}
              {/* Axis badge */}
              <span
                className="text-[8px] px-1 py-px rounded"
                style={{
                  background: (AXIS_COLORS[n.adjacencyAxis] ?? '#6b7280') + '22',
                  color: AXIS_COLORS[n.adjacencyAxis] ?? '#6b7280',
                }}
              >
                {n.adjacencyAxis === 'New Technologies & Capabilities' ? 'New Tech' :
                 n.adjacencyAxis === 'New Markets & Customers' ? 'Markets' :
                 n.adjacencyAxis}
              </span>
              <button
                onClick={() => onRemove(n.id)}
                className="ml-0.5 hover:opacity-80"
                style={{ color: 'var(--text-muted)' }}
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div className="text-[10px] mb-2" style={{ color: 'var(--text-dim)' }}>No related links</div>
      )}

      {/* Add button + dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => { setIsOpen(!isOpen); setSearch(''); }}
          className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg transition-colors"
          style={{
            background: 'var(--glass-bg)',
            border: '1px solid var(--glass-border)',
            color: 'var(--text-muted)',
          }}
        >
          <Plus size={10} />
          Add Related
          <ChevronDown size={10} />
        </button>

        {isOpen && (
          <div
            className="absolute left-0 top-full mt-1 w-56 max-h-48 rounded-lg overflow-hidden z-40"
            style={{
              background: 'var(--modal-bg-heavy)',
              border: '1px solid var(--glass-border)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            }}
          >
            <div className="p-1.5">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search nodes..."
                autoFocus
                className="w-full px-2 py-1 rounded text-[11px]"
                style={{
                  background: 'var(--glass-bg)',
                  border: '1px solid var(--glass-border)',
                  color: 'var(--text-secondary)',
                  outline: 'none',
                }}
              />
            </div>
            <div className="max-h-36 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-[10px]" style={{ color: 'var(--text-dim)' }}>
                  No available nodes
                </div>
              ) : (
                filtered.map(n => (
                  <button
                    key={n.id}
                    onClick={() => {
                      onAdd(n.id);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className="w-full text-left px-3 py-1.5 text-[11px] flex items-center gap-2 transition-colors hover:bg-white/5"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: INITIATIVE_COLORS[n.strategicInitiative] }} />
                    <span className="truncate">{n.name}</span>
                    <span
                      className="ml-auto text-[9px] px-1 py-px rounded shrink-0"
                      style={{
                        background: (AXIS_COLORS[n.adjacencyAxis] ?? '#6b7280') + '22',
                        color: AXIS_COLORS[n.adjacencyAxis] ?? '#6b7280',
                      }}
                    >
                      {n.adjacencyAxis === 'New Technologies & Capabilities' ? 'New Tech' :
                       n.adjacencyAxis === 'New Markets & Customers' ? 'Markets' :
                       n.adjacencyAxis}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
